/**
 * The installer's `config.json` on Azure (IM-47).
 *
 * The same image as AWS, `qrvey-terraform` from 9.4 on, reading the same file
 * — but a different document, so a second input type and a second builder
 * rather than optional fields on `BuildConfigInput`. AWS's `account_config`
 * carries keys and a bucket; Azure's carries a service principal and a
 * resource group, and its region is a *variable*. Folding the two together
 * would make every AWS field optional to the type, which is how a config with
 * no bucket compiles.
 *
 * Everything here was read from image `9.6-20261007-3555`, and
 * `docs/azure/installer-contract.md` is the reading: its last section is what
 * this emits. `fixtures/azure-*.json` pin the output, like `minimal.json` and
 * `full.json` pin AWS's.
 */
import { InvalidDeploymentConfigError, assertNoUndefined, generateDeploymentId, stateKeyFor, } from './config.js';
import { azureDeploymentIdProblem } from './deployment-id.js';
/**
 * Variables the Azure document must never carry, each with the reason.
 *
 * Two break a run, the rest are AWS's and do nothing on Azure:
 *
 * - `resource_group_name` — the CLI appends it to the tfvars itself, and a
 *   second assignment fails with `Attribute redefined` (reproduced against
 *   the image's Terraform 1.9.0).
 * - `subscription_id` — a `-var-file` value beats `TF_VAR_subscription_id`
 *   (also reproduced), so the provider would act in one subscription while
 *   the backend and `az` act in `ARM_SUBSCRIPTION_ID`'s.
 * - The rest are undeclared in the Azure root, which Terraform answers with a
 *   warning and nothing else. Refused anyway: a value that reads as
 *   configuration on a run record and changes nothing is worse than its
 *   absence. `rabbitmq_service_internal` is in this list though the story
 *   did not name it — the Azure root has no such switch, which the image's
 *   `variables.tf` shows.
 *
 * Refused rather than dropped. Each is either a caller that has confused the
 * clouds or this control plane's own AWS injection reaching an Azure run, and
 * both deserve a message rather than a quiet correction.
 */
export const AZURE_REFUSED_VARIABLES = {
    resource_group_name: 'the installer sets resource_group_name from account_config, and a second assignment fails the plan with "Attribute redefined"',
    subscription_id: 'a subscription_id variable overrides account_config.ARM_SUBSCRIPTION_ID for the provider alone, so Terraform would act in one subscription while its state and az act in another',
    image_pull_source: 'image_pull_source chooses between ECR and ACR on AWS; the Azure installer does not declare it',
    iam_permissions_boundary_arn: 'iam_permissions_boundary_arn is an AWS IAM boundary; the Azure installer does not declare it',
    iam_permissions_boundary_required: 'iam_permissions_boundary_required is an AWS IAM boundary; the Azure installer does not declare it',
    opensearch_config: 'opensearch_config is the AWS managed search service; on Azure search is es_config, in the cluster',
    rabbitmq_service_internal: 'rabbitmq_service_internal is an AWS switch; the Azure installer always runs RabbitMQ in the cluster and does not declare it',
};
/**
 * Variables this builder computes and a caller may not override.
 *
 * AWS lets `additionalVariables` override anything, deliberately. On Azure
 * these two would each split one instance into two names: `location` is the
 * region, which on AWS lives in `account_config` where no variable reaches it,
 * so an override would send the instance somewhere its run record does not say
 * (IM-91, one layer down); and `account_config.key` is built from
 * `deployment_id`, so an override would keep the state under one id and the
 * resources under another.
 */
export const AZURE_COMPUTED_VARIABLES = {
    location: 'location is the run’s region, set from AzureBuildConfigInput.location; a second one would deploy somewhere the run record does not say',
    deployment_id: 'deployment_id is set from AzureBuildConfigInput.deploymentId and names the state key too; an override would split the two',
};
/**
 * Why a variables document cannot go to the Azure installer, if anything.
 *
 * Exported for the start path, which IM-46 wires: a run refused there is a
 * 400 naming the variable, where the same refusal at build time is a failed
 * run. It checks names only; values are the contract's other validators'.
 */
export const azureVariablesProblem = (variables) => {
    for (const [name, why] of Object.entries(AZURE_REFUSED_VARIABLES)) {
        if (Object.hasOwn(variables, name))
            return `${name} cannot be sent to Azure: ${why}.`;
    }
    return undefined;
};
const nonEmpty = (value, name) => {
    if (value.trim() === '')
        throw new InvalidDeploymentConfigError(`${name} cannot be empty`);
    return value;
};
/**
 * Builds the Azure config document. Pure, like `buildTerraformConfig`.
 *
 * Throws `InvalidDeploymentConfigError` for an id Azure cannot name resources
 * after, a blank location or state location, or a refused or computed
 * variable among `additionalVariables`. Each is checked here even though the
 * start path should have refused it first, because this is the last place
 * that can before a container starts.
 */
export const buildAzureTerraformConfig = (input, generateId = generateDeploymentId) => {
    const deploymentId = input.deploymentId ?? generateId();
    if (deploymentId.trim() === '') {
        throw new InvalidDeploymentConfigError('deploymentId cannot be empty');
    }
    // Refused, never trimmed: the validators below read the trimmed id, and
    // emitting the padded one would name the state key and every resource after
    // a string that passed no check. A typed id is used exactly as typed
    // (IM-147), so one that is not usable as typed is refused.
    if (deploymentId !== deploymentId.trim()) {
        throw new InvalidDeploymentConfigError('deploymentId has leading or trailing spaces, and the installer would name resources with them');
    }
    const idProblem = azureDeploymentIdProblem(deploymentId);
    if (idProblem !== undefined)
        throw new InvalidDeploymentConfigError(idProblem);
    const additional = input.additionalVariables ?? {};
    const refused = azureVariablesProblem(additional);
    if (refused !== undefined)
        throw new InvalidDeploymentConfigError(refused);
    for (const [name, why] of Object.entries(AZURE_COMPUTED_VARIABLES)) {
        if (Object.hasOwn(additional, name)) {
            throw new InvalidDeploymentConfigError(`${name} cannot be overridden: ${why}`);
        }
    }
    const config = {
        _metadata: { cloud: 'azure' },
        account_config: {
            ARM_CLIENT_ID: nonEmpty(input.identity.clientId, 'identity.clientId'),
            ARM_CLIENT_SECRET: '',
            ARM_TENANT_ID: nonEmpty(input.identity.tenantId, 'identity.tenantId'),
            ARM_SUBSCRIPTION_ID: nonEmpty(input.identity.subscriptionId, 'identity.subscriptionId'),
            resource_group_name: nonEmpty(input.state.resourceGroupName, 'state.resourceGroupName'),
            storage_account_name: nonEmpty(input.state.storageAccountName, 'state.storageAccountName'),
            container_name: nonEmpty(input.state.containerName, 'state.containerName'),
            key: input.state.key ?? stateKeyFor(deploymentId),
        },
        variables: {
            deployment_id: deploymentId,
            location: nonEmpty(input.location, 'location'),
            qrvey_chart_version: input.chartVersion,
            registry_user: input.registry.user,
            registry_key: input.registry.key,
            ...(input.es === undefined ? {} : { es_config: input.es }),
            customer_info: input.customer,
            ...(input.globalization === undefined ? {} : { globalization: input.globalization }),
            initial_admin_email: input.initialAdminEmail,
            enable_trino: input.features?.enableTrino ?? false,
            staging_instance: input.features?.stagingInstance ?? false,
            ...(input.openaiApiKey === undefined ? {} : { openai_api_key: input.openaiApiKey }),
            ...additional,
        },
    };
    assertNoUndefined(config);
    return config;
};
//# sourceMappingURL=azure-config.js.map